<template>
  <sfs-form-modal
    :id="modalId"
    title="Update Billing Details"
    size="lg"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.attention_to"
            label-for="location-attention_to"
          >
            <b-form-input
              id="location-attention_to"
              v-model="$v.form.attention_to.$model"
              :state="$v.form.attention_to.$dirty ? !$v.form.attention_to.$error : null"
              type="text"
              placeholder="ATTN: Accounts Payable"
            />
            <b-form-invalid-feedback
              v-if="$v.form.attention_to.$dirty"
              id="location-attention_to-feedback"
            >
              <span v-if="!$v.form.attention_to.maxLength">
                The attention to must be less than {{ $v.form.attention_to.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.attention_to.serverFailed">{{ serverErrors.attention_to }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col lg="6">
          <b-form-group
            :label="fieldLabels.purchase_order_number"
            label-for="location-purchase_order_number"
          >
            <b-form-input
              id="location-purchase_order_number"
              v-model="$v.form.purchase_order_number.$model"
              autocomplete="new-password"
              :state="$v.form.purchase_order_number.$dirty ? !$v.form.purchase_order_number.$error : null"
              type="text"
            />
            <b-form-invalid-feedback
              v-if="$v.form.purchase_order_number.$dirty"
              id="location-purchase_order_number-feedback"
            >
              <span v-if="!$v.form.purchase_order_number.maxLength">
                The billing PO # must be less than {{ $v.form.purchase_order_number.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.purchase_order_number.serverFailed">{{ serverErrors.purchase_order_number }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label-for="location-deliver_to_organization">
            <b-form-checkbox
              switch
              id="location-deliver_to_organization"
              v-model="$v.form.supply_order_deliver_to_organization.$model"
            >
              {{ fieldLabels.supply_order_deliver_to_organization }}
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.supply_order_delivery_notes"
            label-for="location-delivery_notes"
          >
            <b-form-textarea
              id="location-delivery_notes"
              v-model="$v.form.supply_order_delivery_notes.$model"
              autocomplete="new-password"
              :state="$v.form.supply_order_delivery_notes.$dirty ? !$v.form.supply_order_delivery_notes.$error : null"
            />
            <b-form-invalid-feedback
              v-if="$v.form.supply_order_delivery_notes.$dirty"
              id="location-delivery_notes-feedback"
            >
              <span v-if="!$v.form.supply_order_delivery_notes.maxLength">
                The delivery notes must be less than {{ $v.form.supply_order_delivery_notes.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.supply_order_delivery_notes.serverFailed">{{ serverErrors.supply_order_delivery_notes }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.supply_order_internal_notes"
            label-for="location-internal_notes"
          >
            <b-form-textarea
              id="location-internal_notes"
              v-model="$v.form.supply_order_internal_notes.$model"
              autocomplete="new-password"
              :state="$v.form.supply_order_internal_notes.$dirty ? !$v.form.supply_order_internal_notes.$error : null"
            />
            <b-form-invalid-feedback
              v-if="$v.form.supply_order_internal_notes.$dirty"
              id="location-internal_notes-feedback"
            >
              <span v-if="!$v.form.supply_order_internal_notes.maxLength">
                The internal notes must be less than {{ $v.form.supply_order_internal_notes.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.supply_order_internal_notes.serverFailed">{{ serverErrors.supply_order_internal_notes }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="location-billing-details-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="location-billing-details-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import formMixin from '@/mixins/formMixin';
import { maxLength } from 'vuelidate/lib/validators';
import formModalMixin from '@/mixins/formModalMixin';

export default {
  name: 'LocationsBillingDetailsModal',
  components: {
  },
  mixins: [formMixin, formModalMixin],
  props: {
    value: Object,
  },
  data() {
    return {
      modalId: 'location-billing-details-modal',
      form: {
        attention_to: this.value.attention_to,
        purchase_order_number: this.value.purchase_order_number,
        supply_order_delivery_notes: this.value.supply_order_delivery_notes,
        supply_order_internal_notes: this.value.supply_order_internal_notes,
        supply_order_deliver_to_organization: this.value.supply_order_deliver_to_organization,
      },
      fieldLabels: {
        attention_to: 'Attention To',
        purchase_order_number: 'Billing PO #',
        supply_order_delivery_notes: 'Delivery Notes',
        supply_order_internal_notes: 'Internal Notes (Employees only)',
        supply_order_deliver_to_organization: 'Supplies should be delivered to Organization',
      },
    };
  },
  validations: {
    form: {
      supply_order_delivery_notes: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('supply_order_delivery_notes');
        },
      },
      supply_order_internal_notes: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('supply_order_internal_notes');
        },
      },
      supply_order_deliver_to_organization: {},
      attention_to: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('attention_to');
        },
      },
      purchase_order_number: {
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('purchase_order_number');
        },
      },
    },
  },
  methods: {
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }
      this.$locationsAPI
        .update(this.value.id, this.form)
        .then((location) => {
          this.resetServerErrors();
          this.$v.$reset();
          this.closeModal();
          this.$emit('input', location);
        })
        .catch((error) => {
          this.processServerErrors(error.response.data.errors);
        });
    },
  },
};
</script>
